import React, { useEffect } from 'react';

import {
  FormHelperText,
  Grid,
  InputLabel,
  Slider,
  TextField,
  useTheme,
} from '@mui/material';
import { FilterMacro } from 'src/utils/filters/display-filters';

interface Props {
  displayNutritionalFactFilter: FilterMacro;
  selectedNutritionFactFilter: FilterMacro;
  setSelectedNutritionFactFilter: React.Dispatch<
    React.SetStateAction<FilterMacro>
  >;
}

type EditableRange = {
  min: number | undefined;
  max?: number | null;
};

const normalizeMin = (min: EditableRange['min']) => {
  if (min === undefined || min < 0) {
    return 0;
  }

  return min;
};

const normalizeMax = (max: EditableRange['max']) => {
  if (max === undefined || max === null) {
    return null;
  }

  if (max < 0) {
    return 0;
  }

  return max;
};

const normalizeRangeForParentState = (range: EditableRange) => {
  return {
    min: normalizeMin(range.min),
    max: normalizeMax(range.max),
  };
};

const NutritionFactFilter: React.FC<Props> = ({
  displayNutritionalFactFilter,
  selectedNutritionFactFilter,
  setSelectedNutritionFactFilter,
}) => {
  const theme = useTheme();

  if (!displayNutritionalFactFilter) {
    return null;
  }

  if (!selectedNutritionFactFilter) {
    return null;
  }

  /**
   * We need a temporary local state that'll track every change made by the user in real time, but we
   * want to validate and debounce updates to that state before updating the parent state to limit
   * extraneous catalog re-renders.
   */
  const [tempNutritionFactFilter, setTempNutritionFactFilter] = React.useState<{
    min: number | undefined;
    max?: number | null;
  }>({
    min: selectedNutritionFactFilter.min,
    max: selectedNutritionFactFilter.max,
  });

  useEffect(() => {
    // The parent component can reset all the filters. This ensures the local state reflects that reset.
    setTempNutritionFactFilter(selectedNutritionFactFilter);
  }, [selectedNutritionFactFilter]);

  const invalidRange =
    (tempNutritionFactFilter.min || 0) >
    (tempNutritionFactFilter.max || Infinity);

  const handleBlur = (e: React.FocusEvent<HTMLDivElement>) => {
    const next = e.relatedTarget as Node | null;

    // If the next focused element is inside this container, the user has swapped from modifying
    // one of the inputs to the other, so do not commit the filter update to the parent state yet.
    if (e.currentTarget.contains(next)) {
      return;
    }

    if (invalidRange) {
      // Reset the filter back to its most recent valid state if the user did not enter a valid range
      setTempNutritionFactFilter({
        min: selectedNutritionFactFilter.min,
        max: selectedNutritionFactFilter.max,
      });
      return;
    }

    // Update the parent filter state after normalizing the range
    setSelectedNutritionFactFilter((prevState) => ({
      ...prevState,
      ...normalizeRangeForParentState({
        min: tempNutritionFactFilter.min,
        max: tempNutritionFactFilter.max,
      }),
    }));
  };

  return (
    <Grid
      css={{ padding: 0 }}
      size={{
        xs: 12,
        md: 6,
      }}
    >
      <div
        css={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
          whiteSpace: 'nowrap',
          marginBottom: '10px',
        }}
        onBlur={handleBlur}
      >
        <div>
          <InputLabel
            htmlFor={`${displayNutritionalFactFilter.key}-min-input`}
            sx={{ marginBottom: '8px' }}
            error={invalidRange}
          >
            Min
          </InputLabel>
          <TextField
            id={`${displayNutritionalFactFilter.key}-min-input`}
            css={{ width: '85px', marginRight: '0.1rem' }}
            size="small"
            // This whole '' thing is to render the placeholder when the value is 0
            value={
              tempNutritionFactFilter.min ? tempNutritionFactFilter.min : ''
            }
            error={invalidRange}
            type="number"
            placeholder=""
            onChange={(e) => {
              const minValue = e.target.value
                ? Number(e.target.value)
                : undefined;

              setTempNutritionFactFilter((prevState) => ({
                ...prevState,
                min: minValue,
              }));
            }}
          />
        </div>
        <div>
          <InputLabel
            htmlFor={`${displayNutritionalFactFilter.key}-max-input`}
            sx={{ marginBottom: '8px', textAlign: 'right' }}
            error={invalidRange}
          >
            Max
          </InputLabel>
          <TextField
            id={`${displayNutritionalFactFilter.key}-max-input`}
            css={{ width: '85px', marginLeft: '0.1rem' }}
            size="small"
            placeholder=""
            type="number"
            // This whole '' thing is to have the string "Max" show when the value is set to null
            value={
              tempNutritionFactFilter.max !== null
                ? tempNutritionFactFilter.max
                : ''
            }
            error={invalidRange}
            onChange={(e) => {
              const maxValue = e.target.value
                ? Number(e.target.value)
                : undefined;

              setTempNutritionFactFilter((prevState) => ({
                ...prevState,
                max: maxValue,
              }));
            }}
          />
        </div>
      </div>
      {invalidRange && (
        <FormHelperText error={true}>Min must be less than max</FormHelperText>
      )}
      <div
        css={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '100%',
        }}
      >
        <Slider
          css={{ width: '95%' }}
          valueLabelDisplay="off"
          slotProps={{
            thumb: {
              style: {
                backgroundColor: theme.palette.background.default,
                border: `1px solid ${theme.palette.divider}`,
              },
            },
          }}
          marks={[
            {
              value: displayNutritionalFactFilter.min,
            },
            {
              // These fallback values after the displayNutritionalFactFilter shouldn't be needed, just being defensive
              value: displayNutritionalFactFilter.max ?? 10000,
            },
          ]}
          getAriaLabel={(i) =>
            'slider' + displayNutritionalFactFilter.displayName + i
          }
          value={[
            tempNutritionFactFilter.min ?? displayNutritionalFactFilter.min,
            tempNutritionFactFilter.max ??
              displayNutritionalFactFilter.max ??
              10000,
          ]}
          defaultValue={[
            tempNutritionFactFilter.min || 0,
            tempNutritionFactFilter.max ??
              displayNutritionalFactFilter.max ??
              10000,
          ]}
          min={displayNutritionalFactFilter.min}
          max={displayNutritionalFactFilter.max ?? 10000}
          onChange={(e, newVal: number | number[]) => {
            if (typeof newVal !== 'number') {
              const [min, max] = newVal;

              const hasMinChanged = min !== tempNutritionFactFilter.min;
              if (hasMinChanged) {
                setTempNutritionFactFilter((prevState) => ({
                  ...prevState,
                  min,
                }));
              } else {
                setTempNutritionFactFilter((prevState) => ({
                  ...prevState,
                  max,
                }));
              }
            }
          }}
        />
      </div>
    </Grid>
  );
};

export default NutritionFactFilter;
